/**
 * Framework-free survey normalization helpers shared by the main client and the
 * Sales PWA. Keep this module free of React and Firebase-app singletons so both
 * apps (and the functions codebases) can import it safely.
 */

import { SURVEY_STATUS, type SurveyStatus } from '../../types/survey';

/**
 * Canonical survey status values. The Sales PWA historically wrote the legacy
 * value `'complete'` (singular) instead of the canonical `'completed'`. This
 * normalizer is the single choke point that maps any raw/legacy status read
 * from Firestore onto a canonical {@link SurveyStatus}. Every reader in both
 * apps should route survey `status` through it. Unknown values fall back to
 * `'draft'`.
 *
 * Note: this fixes rendering/derived state on read. A server-side Firestore
 * `where('status', '==', ...)` cannot be normalized on read — for that, either
 * query the legacy alias too (see useSurveyList) or run the one-time backfill.
 */
export function normalizeSurveyStatus(raw: unknown): SurveyStatus {
  switch (raw) {
    // 'submitted': legacy fieldSurveyService value (non-canonical) — those
    // surveys rendered as Draft forever (audit finding B6).
    case 'complete':
    case 'submitted':
    case SURVEY_STATUS.COMPLETED:
      return SURVEY_STATUS.COMPLETED;
    case SURVEY_STATUS.IN_PROGRESS:
      return SURVEY_STATUS.IN_PROGRESS;
    case SURVEY_STATUS.REPORT_GENERATED:
      return SURVEY_STATUS.REPORT_GENERATED;
    case SURVEY_STATUS.ARCHIVED:
      return SURVEY_STATUS.ARCHIVED;
    case SURVEY_STATUS.DRAFT:
      return SURVEY_STATUS.DRAFT;
    default:
      return SURVEY_STATUS.DRAFT;
  }
}

/**
 * Best-effort ISO 8601 string for a Firestore `Timestamp`, an existing ISO
 * string, or a missing/invalid value (falls back to the epoch). Mirrors the
 * shape written by the survey backend, which persists ISO strings for
 * capture/intake timestamps but Firestore `Timestamp`s for `createdAt`/`updatedAt`.
 */
export function toIsoString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return new Date(0).toISOString();
}
